import type { Taxas } from 'Model/ITaxa.js'
import React, { useEffect, useState } from 'react'
import { taxaOptions } from 'View/Page/HomePage/01-CreateOrderPage/TaxaOptions.tsx'
import type { SampleConfigurationTypeKey } from '../../../../Generated/SampleConfigurationType.js'
import { SampleConfigurationTypes } from '../../../../Generated/SampleConfigurationType.js'
import type { RedlistModeKey } from '../../../../Generated/RedlistMode.js'
import { RedlistModes } from '../../../../Generated/RedlistMode.js'
import { useGetAuthenticatedUser } from '../../../../Flux/Selector/index.js'
import type { BaseModalProps } from '../../../Modal/BaseModal.js'
import { BaseModal } from '../../../Modal/BaseModal.js'
import Actions from '../../../../Flux/index.js'
import { orderSlice } from '../../../../Flux/Slice/Order/OrderSlice.js'
import { useDispatch } from 'react-redux'
import { useModal } from '../../../Modal/UseModal.js'
import { TargetRegions } from '../../../../Generated/TargetRegion.ts'

export interface ClaimAsLabModalProps {
  orderId: string
  tokenChecksum?: string
  targetTaxa: Taxas | null
}

export const ClaimAsLabModal = (props: ClaimAsLabModalProps & BaseModalProps) => {
  const [taxa, setTaxa] = useState<Taxas | 'disabled'>(props.targetTaxa || 'disabled')
  const [allowSingletons, setAllowSingleton] = useState(false)
  const [region, setRegion] = useState<string | null>(null)
  const [sampleConfiguration, setSampleConfiguration] = useState<SampleConfigurationTypeKey | undefined>(undefined)
  const [redlistMode, setRedlistMode] = useState<RedlistModeKey | undefined>(undefined)
  const user = useGetAuthenticatedUser()
  const dispatch = useDispatch()

  const { openModal } = useModal()

  useEffect(() => {
    if (user) {
      setSampleConfiguration(user.org.defaultSampleConfigurationType)
      setRedlistMode(user.org.defaultRedlistMode)
    }
  }, [user])

  const handleOK = () => {
    if (!taxa || taxa === 'disabled') {
      alert('Please select a taxonomy')
      return
    }

    dispatch(
      Actions[orderSlice.name].claimOrderRequest({
        orderId: props.orderId,
        token: props.tokenChecksum,
        targetTaxa: taxa,
        targetRegion: taxa === 'CO1_ANIMAL' ? (region ?? undefined) : undefined,
        allowSingletons,
        sampleConfiguration: sampleConfiguration ?? 'SINGLE_SAMPLE_PAIRS',
        redlistMode: redlistMode ?? user?.org.defaultRedlistMode ?? 'NONE'
      })
    )

    props.onClose()
  }

  return (
    <BaseModal onClose={props.onClose} isOpen={props.isOpen}>
      <div className="modal-box max-w-6xl">
        <h3 className="font-bold text-2xl mb-4">Claim Order as Lab</h3>
        <div className="text-base mb-4">
          <p>Are you sure you want to claim this order as a lab?</p>
          <p>This action cannot be undone.</p>
          <p>Order ID: {props.orderId}</p>
        </div>
        <div className="gap-y-2 flex flex-col">
          <div className="flex justify-between">
            <label className="text-base">Taxonomy</label>
            <select
              data-testid="select-taxa"
              className="select select-bordered w-full max-w-xs"
              value={taxa}
              onChange={(e) => setTaxa(e.target.value as Taxas)}
            >
              <option value={'disabled'} disabled>
                Select Taxonomy (required)
              </option>
              {taxaOptions.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>
          <div className="flex justify-between">
            <label className="text-base">Target Region</label>
            <select
              className="select select-bordered w-full max-w-xs"
              value={region || ''}
              onChange={(e) => setRegion(e.target.value || null)}
              disabled={taxa !== 'CO1_ANIMAL'}
            >
              {Object.values(TargetRegions).map((r) => (
                <option key={r.key ?? 'null'} value={r.key ?? ''}>
                  {r.label}
                </option>
              ))}
            </select>
          </div>
          <div className="flex justify-between">
            <label className="text-base">Sample Configuration</label>
            <div className={'flex flex-col gap-2'}>
              <select
                className="select select-bordered w-full max-w-xs"
                value={sampleConfiguration}
                onChange={(e) => setSampleConfiguration(e.target.value as SampleConfigurationTypeKey)}
              >
                <option value={'disabled'} disabled>
                  Select Sample Configuration (required)
                </option>
                {Object.values(SampleConfigurationTypes).map((t) => (
                  <option key={t.key} value={t.key}>
                    {t.label}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className={'btn btn-outline btn-xs'}
                onClick={() =>
                  openModal(
                    'sampleConfigurationType',
                    {
                      initial: sampleConfiguration ? user?.org?.defaultSampleConfigurationType : undefined
                    },
                    (result) => setSampleConfiguration(result)
                  )
                }
              >
                Help me choose
              </button>
            </div>
          </div>
          <div className="flex justify-between">
            <label className="text-base">allow Singleton</label>
            <input
              type="checkbox"
              className="checkbox checkbox-primary"
              checked={allowSingletons}
              onChange={(e) => setAllowSingleton(e.target.checked)}
            />
          </div>
          <div className="flex justify-between">
            <label className="text-base">Redlist Mode</label>
            <select
              className="select select-bordered w-full max-w-xs"
              value={redlistMode}
              onChange={(e) => setRedlistMode(e.target.value as RedlistModeKey)}
            >
              {Object.entries(RedlistModes).map(([key, { label }]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="modal-action">
          <button className="btn" onClick={() => props.onClose()}>
            Close
          </button>
          <button data-testid="claim-submit" className="btn btn-primary" onClick={handleOK}>
            OK
          </button>
        </div>
      </div>
    </BaseModal>
  )
}
